import { forwardRef, RefObject } from 'react';

// Utils
import { combineClasses } from '@shared/utils';

// Types
import { UserProfile } from '@/types';

// Components
import { UserMentionItem } from './UserMentionItem';
interface UserMentionDropdownProps {
  users: UserProfile[];
  isLoading: boolean;
  selectedIndex: number;
  selectedUserRef: RefObject<HTMLButtonElement | null>;
  isVisible: boolean;
  onUserSelect: (user: UserProfile) => void;
}
export const UserMentionDropdown = forwardRef<HTMLDivElement, UserMentionDropdownProps>(({
  users,
  isLoading,
  selectedIndex,
  selectedUserRef,
  isVisible,
  onUserSelect
}, ref) => {
  if (!isVisible) return null;

  // Render loading skeleton
  const renderLoadingItem = () => <div className="py-2 px-3 h-12" data-testid="user-mention-loading" data-sentry-component="renderLoadingItem" data-sentry-source-file="index.tsx">
        <div className="h-full flex-center gap-3.5 animate-pulse">
          <div className="size-7.5 bg-gray-200 rounded-full" />
          <div className="h-6 bg-gray-200 rounded flex-1" />
        </div>
      </div>;

  // Render empty state
  const renderEmptyState = () => <div data-testid="user-mention-empty" role="status" aria-live="polite" className="p-3 text-center text-gray-500 text-sm" data-sentry-component="renderEmptyState" data-sentry-source-file="index.tsx">
        No matching users found
      </div>;

  // Render content based on loading state and users data
  const renderContent = () => {
    if (isLoading) return renderLoadingItem();

    // Only show empty state if we have actually searched
    if (!isLoading && !users?.length) return renderEmptyState();
    return users.map((user, index) => <UserMentionItem key={user.id} user={user} index={index} isSelected={index === selectedIndex} selectedUserRef={selectedUserRef} onUserSelect={onUserSelect} />);
  };
  return <div ref={ref} className={combineClasses('absolute top-full bottom-auto md:bottom-full md:top-auto my-2 left-0 right-0', 'bg-white border border-border-500 rounded-lg shadow-lg', 'max-h-60 overflow-y-auto z-[100] small-scrollbar')} id="user-mention-dropdown" data-testid="user-mention-dropdown" role="listbox" aria-label="User mention suggestions" aria-live="polite" aria-expanded={isVisible}>
        {renderContent()}
      </div>;
});
UserMentionDropdown.displayName = 'UserMentionDropdown';